import { useCallback, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import qs from 'qs'
import oikosApi from '../../../services/oikos'
import { getRegs, selectValues } from '../../../utils/fc'
import {
  formatBeneficiosForStorage,
  formatMultiSelectIdsForStorage,
  resolveDocumentosObrigatoriosLabelsFromTpDocumento,
} from '../ModalFormContrForn/contrFornExtendedFields'
import { notifyError, notifySuccess } from '../../../utils/notify'
import {
  buildInhireCreateJobBody,
  mapInhireFormToPedidoCompraFields,
  validateForm,
  validateOikosForm,
} from './FormularioAbrirVagaInhireApi'

const INHIRE_API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://oikos.ecoss.is/oikos/_inhire_api.php?action='
  : '/api/integration/'

const VIEW_RUBR_ORC_ITEM_PROJ_ATUACAO_ID_FIELD = "(SELECT projAtuacao_id FROM projAtuacao WHERE projAtuacao.projAtuacao_id = (SELECT projAtuacao_id FROM propOrcItem WHERE propOrcItem.propOrcItem_id = (SELECT propOrcItem_id FROM orcItem WHERE orcItem.orcItem_id = viewRubr_OrcItem.orcItem_id LIMIT 1) LIMIT 1) LIMIT 1)"

const fetchOrcItemContratoFornId = async (orcItemId) => {
  const normalizedOrcItemId = Number(orcItemId) || 0
  if (!normalizedOrcItemId) return 0

  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'orcItem',
      data: { orcItem_id: normalizedOrcItemId },
    })}`)

    return Number(res?.data?.[0]?.contratoForn_id) || 0
  } catch (e) {
    console.log('Erro ao buscar contratoForn_id do orcItem:', e)
    return 0
  }
}

const fetchContratosInternosOptions = async () => {
  const res = await fetch('https://oikos.ecoss.is/oikos/planilha.listload.php?lista=contratoForn')
  const data = await res.json()
  return Array.isArray(data) ? data : []
}

const fetchProjAtuacaoIdForOrcItem = async (orcItemId) => {
  const normalizedOrcItemId = Number(orcItemId) || 0
  if (!normalizedOrcItemId) return null

  const resp = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'viewRubr_OrcItem',
    data: {
      orcItem_id: normalizedOrcItemId,
      orcItem_ativo: 1,
    },
    fields: {
      "(SELECT pf_id FROM orcItem WHERE orcItem.orcItem_id = viewRubr_OrcItem.orcItem_id LIMIT 1)" : "pf_id",
      [VIEW_RUBR_ORC_ITEM_PROJ_ATUACAO_ID_FIELD]: 'projAtuacao_id',
    },
  })}`)

  const projAtuacaoId = Number(resp?.data?.[0]?.projAtuacao_id) || 0
  return projAtuacaoId || null
}

const buildInhireJobName = (pedidoCompraData) => (
  (pedidoCompraData.vaga_cargo || pedidoCompraData.vagaCargo || pedidoCompraData.pedidoCompra_desc || pedidoCompraData.vaga_descricao || '').trim()
)

const resolveDocumentosObrigatoriosLabel = async (pedidoCompraData) => {
  const raw = pedidoCompraData.vaga_documentosObrigatorios ?? pedidoCompraData.vagaDocumentosObrigatorios
  return resolveDocumentosObrigatoriosLabelsFromTpDocumento(raw)
}

const buildInhireJobDescription = (pedidoCompraData) => {
  const sections = [
    pedidoCompraData.pedidoCompra_desc || pedidoCompraData.vaga_descricao,
    pedidoCompraData.vaga_escopo && `Escopo: ${pedidoCompraData.vaga_escopo}`,
    pedidoCompraData.vaga_requisitos && `Requisitos: ${pedidoCompraData.vaga_requisitos}`,
    pedidoCompraData.vaga_formacao && `Formacao: ${pedidoCompraData.vaga_formacao}`,
    pedidoCompraData.vaga_condicoesTrabalho && `Condicoes de trabalho: ${pedidoCompraData.vaga_condicoesTrabalho}`,
    pedidoCompraData.vaga_fornecedorEpis && `Fornecedor dos EPIs: ${pedidoCompraData.vaga_fornecedorEpis}`,
    pedidoCompraData.vaga_beneficios && `Beneficios: ${pedidoCompraData.vaga_beneficios}`,
    pedidoCompraData.vaga_documentosObrigatorios && `Documentos obrigatorios: ${pedidoCompraData.vaga_documentosObrigatorios}`,
  ].filter(Boolean)

  return sections.join('\n\n')
}

const resolveInhireLocationFromPedidoCompra = async (pedidoCompraData) => {
  const [ufs, municipios] = await Promise.all([
    selectValues('uf', 'uf_id', 'uf_nome', {}, { uf_sigla: 'uf_sigla' }),
    selectValues('mun', 'mun_id', 'mun_nome'),
  ])

  const estadoId = pedidoCompraData.vagaEstado || pedidoCompraData.vaga_localidadeEstado
  const cidadeId = pedidoCompraData.vagaCidade || pedidoCompraData.vaga_localidadeCidade
  const uf = (ufs || []).find((item) => Number(item.value) === Number(estadoId))
  const municipio = (municipios || []).find((item) => Number(item.value) === Number(cidadeId))

  return {
    locationCity: municipio?.label || '',
    locationState: uf?.uf_sigla || '',
  }
}

export const createInhireJobFromPedidoCompra = async (pedidoCompraData) => {
  const jobName = buildInhireJobName(pedidoCompraData)
  if (!jobName) {
    throw new Error('Nome da vaga nao informado.')
  }

  const { locationCity, locationState } = await resolveInhireLocationFromPedidoCompra(pedidoCompraData)
  if (!locationCity || !locationState) {
    throw new Error('Cidade e estado da vaga nao informados.')
  }

  const documentosObrigatoriosLabel = await resolveDocumentosObrigatoriosLabel(pedidoCompraData)

  const jobData = {
    name: jobName,
    description: buildInhireJobDescription({
      ...pedidoCompraData,
      vaga_documentosObrigatorios: documentosObrigatoriosLabel,
    }),
    status: 'open',
    contractType: [pedidoCompraData.vagaContrato || pedidoCompraData.vaga_tipoContrato || 'CLT'],
    locationCity,
    locationState,
    locationCountry: 'BR',
    locationRequired: false,
    talentSuggestions: false,
    postingPage: {
      status: 'published',
      displayName: jobName,
      activeJobBoards: [],
    },
  }

  return axios.post(`${INHIRE_API_BASE}create-job`, jobData)
}

const PEDIDO_COMPRA_STATUS_APROVADO = 5

const fetchPedidoCompraVagaInhireData = async (pedidoCompraId) => {
  const pedidoCompraIdNormalizado = Number(pedidoCompraId) || 0
  if (!pedidoCompraIdNormalizado) return null

  const [pedidoCompra] = await getRegs('pedidoCompra', { pedidoCompra_id: pedidoCompraIdNormalizado })
  const [vaga] = await getRegs('vaga', { pedidoCompra_id: pedidoCompraIdNormalizado })
  if (!pedidoCompra || !vaga) return null

  return {
    ...pedidoCompra,
    ...vaga,
  }
}

const extractInhireJobId = (createResponseData) => {
  const rawId = createResponseData?.vaga?.id
    ?? createResponseData?.id
    ?? createResponseData?.jobId
    ?? createResponseData?.vaga?.jobId
  if (rawId == null || rawId === '') return null
  return String(rawId)
}

const persistVagaInhireId = async (vagaId, inhireJobId) => {
  const normalizedVagaId = Number(vagaId) || 0
  const normalizedInhireId = inhireJobId == null || inhireJobId === ''
    ? null
    : String(inhireJobId)
  if (!normalizedVagaId || !normalizedInhireId) return false

  await oikosApi.post('/', {
    action: 'update',
    table: 'vaga',
    data: {
      vaga_id: normalizedVagaId,
      vaga_inhireId: normalizedInhireId,
    },
  })
  return true
}

const formatInhireRequestError = (requestError) => {
  const details = requestError?.response?.data
  const raw = typeof details === 'string'
    ? details
    : (details?.message || requestError?.message || '')
  const message = typeof raw === 'string' ? raw : JSON.stringify(raw)
  if (!message || /<!DOCTYPE|<html/i.test(message)) {
    return 'Não foi possível criar a vaga no inHire.'
  }
  return message
}

const hydrateInhireFormLocation = async (form = {}) => {
  if (form.workModel === 'remoto') return form
  if (form.locationState && form.locationCity) return form

  const [ufs, municipios] = await Promise.all([
    selectValues('uf', 'uf_id', 'uf_nome', {}, { uf_sigla: 'uf_sigla' }),
    selectValues('mun', 'mun_id', 'mun_nome'),
  ])

  const uf = (ufs || []).find((item) => Number(item.value) === Number(form.locationUfId))
  const municipio = (municipios || []).find((item) => Number(item.value) === Number(form.locationMunId))

  return {
    ...form,
    locationState: String(form.locationState || uf?.uf_sigla || '').trim().toUpperCase(),
    locationCity: form.locationCity || municipio?.label || '',
  }
}

const createInhireJobFromForm = async (form) => (
  axios.post(`${INHIRE_API_BASE}create-job`, buildInhireCreateJobBody(form))
)

const createAndPersistInhireJobFromForm = async ({ vagaId, existingInhireId, inhireForm }) => {
  const normalizedVagaId = Number(vagaId) || 0
  const existingId = String(existingInhireId || '').trim()
  if (!inhireForm) {
    return { ok: true, skipped: true, inhireJobId: existingId || null }
  }
  if (existingId) {
    return { ok: true, alreadyExists: true, inhireJobId: existingId }
  }
  if (!normalizedVagaId) {
    notifyError('Vaga salva no Oikos, mas não foi possível identificar o id para gravar o inHire.')
    return { ok: false, inhireJobId: null }
  }

  try {
    const hydratedForm = await hydrateInhireFormLocation(inhireForm)
    const createResponse = await createInhireJobFromForm(hydratedForm)
    const inhireJobId = extractInhireJobId(createResponse?.data)
    if (!inhireJobId) {
      notifyError('Vaga salva no Oikos, mas o id do inHire não veio na resposta.')
      return { ok: false, inhireJobId: null }
    }

    try {
      await persistVagaInhireId(normalizedVagaId, inhireJobId)
    } catch (persistError) {
      console.log('Vaga criada no inHire, mas falhou ao gravar vaga_inhireId:', persistError)
      notifyError('Vaga criada no inHire, mas não foi possível salvar o id no Oikos.')
      return { ok: false, inhireJobId }
    }

    return { ok: true, inhireJobId }
  } catch (requestError) {
    console.log('Erro ao criar vaga no inHire:', requestError)
    if (requestError.response?.data) {
      console.log('Detalhes inHire:', requestError.response.data)
    }
    notifyError(`Vaga salva no Oikos, mas não foi possível criar no inHire. ${formatInhireRequestError(requestError)}`)
    return { ok: false, inhireJobId: null }
  }
}

const mergePedidoCompraWithInhireForm = (pedidoCompraData = {}, inhireForm = null) => {
  if (!inhireForm) return pedidoCompraData
  const mapped = mapInhireFormToPedidoCompraFields(inhireForm)
  return {
    ...pedidoCompraData,
    ...mapped,
    pedidoCompra_vlrLimite: pedidoCompraData.pedidoCompra_vlrLimite || mapped.vaga_salarioMaximo || mapped.vaga_salarioAlvo || 0,
  }
}

const resolveVagaIdAfterSave = async ({ pedidoCompraId, insertResponse, currentVagaId }) => {
  const fromInsert = Number(insertResponse?.data?.[0]?.vaga_id) || 0
  if (fromInsert) return fromInsert
  const fromCurrent = Number(currentVagaId) || 0
  if (fromCurrent) return fromCurrent
  const vaga = await fetchVagaByPedidoCompraId(pedidoCompraId)
  return Number(vaga?.vaga_id) || 0
}

const persistVagaHorarios = async (vagaId, horarios) => {
  const id = Number(vagaId) || 0
  const lista = Array.isArray(horarios) ? horarios : []
  if (!id) {
    if (lista.length) {
      notifyError('A vaga foi salva, mas os horários da semana não puderam ser gravados.')
    }
    return
  }

  const atuais = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'vagaHorario',
    data: { vaga_id: id },
  })}`)
  const rows = Array.isArray(atuais?.data) ? atuais.data : []
  await Promise.all(rows.map((row) => oikosApi.post('/', {
    action: 'delete',
    table: 'vagaHorario',
    data: { vagaHorario_id: Number(row.vagaHorario_id) },
  })))
  await Promise.all(lista.map((item) => oikosApi.post('/', {
    action: 'insert',
    table: 'vagaHorario',
    data: {
      vaga_id: id,
      diaSemana_id: Number(item.diaSemana_id),
      vagaHorario_horarioInicio: item.vagaHorario_horarioInicio,
      vagaHorario_horarioFim: item.vagaHorario_horarioFim,
    },
  })))
}

const abrirVagaInhireParaPedidoCompra = async (pedidoCompraId) => {
  const pedidoCompraVagaData = await fetchPedidoCompraVagaInhireData(pedidoCompraId)
  if (!pedidoCompraVagaData) {
    notifyError('Não foi possível encontrar a cotação ou vaga.')
    return { ok: false, inhireJobId: null, vagaId: null }
  }

  const existingInhireId = String(pedidoCompraVagaData.vaga_inhireId || '').trim()
  if (existingInhireId) {
    return {
      ok: true,
      inhireJobId: existingInhireId,
      vagaId: Number(pedidoCompraVagaData.vaga_id) || 0,
      alreadyExists: true,
    }
  }

  try {
    const createResponse = await createInhireJobFromPedidoCompra(pedidoCompraVagaData)
    console.log('InHire vaga criada:', createResponse.data)

    const inhireJobId = extractInhireJobId(createResponse?.data)
    const vagaId = Number(pedidoCompraVagaData.vaga_id) || 0
    if (inhireJobId && vagaId) {
      try {
        await persistVagaInhireId(vagaId, inhireJobId)
      } catch (persistError) {
        console.log('Vaga criada no inHire, mas falhou ao gravar vaga_inhireId:', persistError)
        notifyError('Vaga criada no inHire, mas não foi possível salvar o id no Oikos.')
        return { ok: false, inhireJobId, vagaId }
      }
    } else if (!inhireJobId) {
      console.log('InHire criou a vaga, mas o id não veio na resposta:', createResponse?.data)
      notifyError('Vaga criada no inHire, mas o id não veio na resposta.')
      return { ok: false, inhireJobId: null, vagaId }
    }

    notifySuccess('Vaga aberta no inHire com sucesso.')
    return { ok: true, inhireJobId, vagaId }
  } catch (e) {
    notifyError('Erro ao criar vaga no inHire.')
    console.log('Erro ao criar vaga no inHire:', e)
    if (e.response?.data) {
      console.log('Detalhes inHire:', e.response.data)
    }
    return { ok: false, inhireJobId: null, vagaId: Number(pedidoCompraVagaData.vaga_id) || 0 }
  }
}

const savePedidoCompraOuVagaDocuments = async (files = [], id, table) => {
  const pedidoCompraOuVagaIdNormalizado = Number(id) || 0
  const filesValidos = (files || []).filter((file) => file?.data && !file?.props?.isPersisted)

  if (!pedidoCompraOuVagaIdNormalizado || filesValidos.length <= 0) return

  await Promise.all(filesValidos.map(async (file) => {
    const defaultTpDocumentoId = table === 'compraCotacao' ? 37 : 35
    const tpDocumentoId = Number(file?.props?.documentType?.tpDocumento_id) || defaultTpDocumentoId

    const documentCreated = await oikosApi.post('/', {
      action: 'insert',
      table: 'documento',
      data: {
        tpDocumento_id: tpDocumentoId,
        documento_desc: file.props?.description || file.data.path,
        documento_iniVig: undefined,
        documento_fimVig: undefined,
        documento_dtEmissao: undefined,
        documento_prazo: undefined,
        documento_ref: undefined,
        privacidade_id: 2,
      },
    })

    const documentoId = Number(documentCreated?.data?.[0]?.documento_id) || 0
    if (!documentoId) return

    await oikosApi.post('/', {
      action: 'insert',
      table: 'documentoVinc',
      data: {
        documento_id: documentoId,
        documentoVinc_tabRef: table,
        documentoVinc_tabId: pedidoCompraOuVagaIdNormalizado,
      },
    })

    const formData = new FormData()
    formData.append('file', new Blob([file.data], { type: file.data.type }), file.data.path)
    formData.append('table', 'arquivo')
    formData.append('action', 'upload_file')
    formData.append('arquivo_tabRef', 'documento')
    formData.append('arquivo_tabId', documentoId)
    formData.append('arquivo_privacidade', documentCreated.data[0].privacidade_id)
    formData.append('arquivo_desc', file.data.path)

    await oikosApi.post('/', formData)
  }))
}

const moneyOrNull = (value) => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const timeOrNull = (value) => {
  const raw = String(value || '').trim()
  if (/^\d{2}:\d{2}$/.test(raw)) return `${raw}:00`
  if (/^\d{2}:\d{2}:\d{2}$/.test(raw)) return raw
  return null
}

const bitOrZero = (value) => (Number(value) === 1 ? 1 : 0)

const acessoCorporativo = (pedidoCompra) => (
  bitOrZero(pedidoCompra.vaga_office365) || bitOrZero(pedidoCompra.vaga_emailCorp) ? 1 : 0
)

const fimVigenciaParaGravar = (pedidoCompra) => (
  pedidoCompra.vaga_dtVigFim
  || pedidoCompra.pedidoCompra_dtVigFim
  || null
)

const buildVagaDataFromPedidoCompra = (pedidoCompra) => ({
  vaga_tipoContrato: pedidoCompra.vaga_tipoContrato || pedidoCompra.vagaContrato,
  vaga_localidadeEstado: pedidoCompra.vaga_localidadeEstado || pedidoCompra.vagaEstado,
  vaga_localidadeCidade: pedidoCompra.vaga_localidadeCidade || pedidoCompra.vagaCidade,
  vaga_nome: pedidoCompra.vaga_nome || pedidoCompra.vagaNome,
  vaga_descricao: pedidoCompra.vaga_descricao || pedidoCompra.pedidoCompra_desc,
  vaga_salarioAlvo: moneyOrNull(pedidoCompra.vaga_salarioAlvo),
  vaga_salarioMinimo: moneyOrNull(pedidoCompra.vaga_salarioMinimo),
  vaga_salarioMaximo: moneyOrNull(pedidoCompra.vaga_salarioMaximo),
  vaga_dtValidade: pedidoCompra.vaga_dtValidade || null,
  vaga_horarioIni: timeOrNull(pedidoCompra.vaga_horarioIni),
  vaga_horarioFim: timeOrNull(pedidoCompra.vaga_horarioFim),
  vaga_office365: acessoCorporativo(pedidoCompra),
  vaga_emailCorp: acessoCorporativo(pedidoCompra),
  vaga_dtVigFim: fimVigenciaParaGravar(pedidoCompra),
  vaga_dtVigIni: pedidoCompra.vaga_dtVigIni || pedidoCompra.pedidoCompra_dtVigIni,
  vaga_formacao: pedidoCompra.vaga_formacao || pedidoCompra.vagaFormacao,
  vaga_cargo: pedidoCompra.vaga_cargo || pedidoCompra.vagaCargo,
  vaga_dtLimiteEntrega: pedidoCompra.vaga_dtLimiteEntrega || pedidoCompra.pedidoCompra_dtLimiteEntrega,
  vaga_requisitos: pedidoCompra.vaga_requisitos || pedidoCompra.vagaRequisitos,
  vaga_documentosObrigatorios: formatMultiSelectIdsForStorage(
    pedidoCompra.vaga_documentosObrigatorios ?? pedidoCompra.vagaDocumentosObrigatorios,
  ),
  vaga_beneficios: formatBeneficiosForStorage(pedidoCompra.vaga_beneficios ?? pedidoCompra.vagaBeneficios),
  vaga_condicoesTrabalho: pedidoCompra.vaga_condicoesTrabalho || pedidoCompra.vagaCondicoesTrabalho,
  vaga_fornecedorEpis: pedidoCompra.vaga_fornecedorEpis || pedidoCompra.vagaFornecedorEpis,
  vaga_escopo: pedidoCompra.vaga_escopo || pedidoCompra.vagaEscopo,
  orcItem_id: pedidoCompra.orcItem_id,
  pedidoCompra_id: pedidoCompra.pedidoCompra_id,
})

const insertVaga = async (pedidoCompra) => {
  const res = await oikosApi.post('/', {
    action: 'insert',
    table: 'vaga',
    data: {
      ...buildVagaDataFromPedidoCompra(pedidoCompra),
      vagaStatus_id: 1,
    },
  })

  return res
}

const updateVaga = async (pedidoCompra) => {
  const vagaId = Number(pedidoCompra.vaga_id) || 0
  if (!vagaId) return null

  return oikosApi.post('/', {
    action: 'update',
    table: 'vaga',
    data: {
      vaga_id: vagaId,
      ...buildVagaDataFromPedidoCompra(pedidoCompra),
    },
  })
}

const fetchVagaByPedidoCompraId = async (pedidoCompraId) => {
  const normalizedPedidoCompraId = Number(pedidoCompraId) || 0
  if (!normalizedPedidoCompraId) return null

  const res = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'vaga',
    data: { pedidoCompra_id: normalizedPedidoCompraId },
  })}`)

  return res?.data?.[0] || null
}

const resolveVagaIdForEquipePedidoCompra = async ({
  pedidoCompraData = {},
  editPedidoCompraRow = null,
} = {}) => {
  const fromData = Number(pedidoCompraData.vaga_id) || 0
  if (fromData) return fromData

  const fromEditRow = Number(editPedidoCompraRow?.vaga_id) || 0
  if (fromEditRow) return fromEditRow

  const pedidoCompraId = Number(
    pedidoCompraData.pedidoCompra_id || editPedidoCompraRow?.pedidoCompra_id,
  ) || 0
  if (!pedidoCompraId) return 0

  const vaga = await fetchVagaByPedidoCompraId(pedidoCompraId)
  return Number(vaga?.vaga_id) || 0
}

const insertCandidatoFromCompraCotacao = async ({
  vagaId,
  compraCotacaoId,
  nome,
  valorSalario,
  userId,
} = {}) => {
  const normalizedVagaId = Number(vagaId) || 0
  const normalizedCompraCotacaoId = Number(compraCotacaoId) || 0
  if (!normalizedVagaId || !normalizedCompraCotacaoId) {
    throw new Error('Vaga ou cotação inválida para cadastro do candidato.')
  }

  const response = await oikosApi.post('/', {
    action: 'insert',
    table: 'candidato',
    data: {
      vaga_id: normalizedVagaId,
      compraCotacao_id: normalizedCompraCotacaoId,
      candidato_nome: nome ? String(nome).trim() : undefined,
      candidato_valorSalario: valorSalario != null && valorSalario !== ''
        ? Number(valorSalario) || 0
        : undefined,
      candidato_ativo: 1,
      candidato_usuarioCria: userId,
      candidato_usuarioMod: userId,
    },
  })

  return response?.data?.[0] || null
}

const normalizeOrcItemId = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export function usePedidoCompraEquipeActions({
  pedidoCompraData,
  setPedidoCompraData,
  filesPedidoCompra = [],
  user,
  showEquipePedidoCompraFields = false,
  vagaTipo = '',
  onSuccess,
  onClose,
  createLinkedTasks,
}) {
  const [selContratosInternos, setSelContratosInternos] = useState([])
  const [selOrcItemsOriginais, setSelOrcItemsOriginais] = useState([])
  const [isLoadingContratosInternos, setIsLoadingContratosInternos] = useState(false)
  const [isLoadingOrcItemsOriginais, setIsLoadingOrcItemsOriginais] = useState(false)
  const [isSavingContratoInterno, setIsSavingContratoInterno] = useState(false)
  const [isSavingPedidoCompraEquipe, setIsSavingPedidoCompraEquipe] = useState(false)
  const [isCreatingInhireVaga, setIsCreatingInhireVaga] = useState(false)

  const loadContratosInternos = useCallback(async () => {
    try {
      setIsLoadingContratosInternos(true)
      setSelContratosInternos(await fetchContratosInternosOptions())
    } catch (e) {
      setSelContratosInternos([])
      notifyError('Erro ao carregar contratos internos.')
      console.log('Erro ao carregar contratos internos:', e)
    } finally {
      setIsLoadingContratosInternos(false)
    }
  }, [])

  const loadOrcItemsOriginais = useCallback(async () => {
    try {

      setIsLoadingOrcItemsOriginais(true)

      const dataOrcItems = {
        orcItem_ativo: 1,
        // Itens de origem: categorias RH/equipe específicas
        categ_id: '!IN!(165,34,55,42)',
      }

      const resp = await oikosApi.get(`/?${qs.stringify({
        action: 'get',
        table: 'viewRubr_OrcItem',
        fields: {
          orcItem_id: 'value',
          item_orcamentario: 'label',
          "(SELECT pf_id FROM orcItem WHERE orcItem.orcItem_id = viewRubr_OrcItem.orcItem_id LIMIT 1)" : "pf_id",
        },
        data: dataOrcItems,
        order: 'label',
      })}`)

      console.log('resp originais', resp.data)

      setSelOrcItemsOriginais(Array.isArray(resp.data) ? resp.data : [])
    } catch (e) {
      setSelOrcItemsOriginais([])
      notifyError('Erro ao carregar itens orcamentarios.')
      console.log('Erro ao carregar itens orcamentarios:', e)
    } finally {
      setIsLoadingOrcItemsOriginais(false)
    }
  }, [])

  const updateOrcItemOriginal = useCallback(async () => {
    const orcItemId = normalizeOrcItemId(pedidoCompraData?.orcItem_id)
    const orcItemOriginal = normalizeOrcItemId(pedidoCompraData?.orcItem_original)

    if (!orcItemId || !orcItemOriginal) {
      notifyError('Selecione o item orcamentario de origem.')
      return false
    }

    try {
      await oikosApi.post('/', {
        action: 'update',
        table: 'orcItem',
        data: {
          orcItem_id: orcItemId,
          orcItem_original: orcItemOriginal,
        },
      })
      return true
    } catch (e) {
      notifyError('Erro ao atualizar item orcamentario de origem.')
      console.log('Erro ao atualizar orcItem_original:', e)
      return false
    }
  }, [pedidoCompraData?.orcItem_id, pedidoCompraData?.orcItem_original])

  const submitPedidoCompraEquipe = useCallback(async (inhireForm = null, { createInhire = false } = {}) => {
    const data = mergePedidoCompraWithInhireForm(pedidoCompraData, inhireForm)

    if (!data?.orcItem_id) {
      notifyError('Nao foi possivel identificar o item orcamentario.')
      return null
    }

    if (!data?.pedidoCompra_desc?.trim()) {
      notifyError('Preencha o nome ou a descrição da vaga.')
      return null
    }

    let orcItemPfId = Number(data.pf_id) || 0
    if (!orcItemPfId) {
      try {
        const res = await oikosApi.get(`/?${qs.stringify({
          action: 'get',
          table: 'orcItem',
          data: { orcItem_id: Number(data.orcItem_id) },
        })}`)
        orcItemPfId = Number(res?.data?.[0]?.pf_id) || 0
      } catch (e) {
        console.log('Erro ao buscar pf_id do orcItem:', e)
      }
    }
    if (!orcItemPfId) {
      notifyError('Item orçamentário sem responsável')
      return null
    }

    const pedidoCompraId = Number(data.pedidoCompra_id) || 0
    const isEditing = Boolean(pedidoCompraId)

    try {
      if (isEditing) {
        await oikosApi.post('/', {
          action: 'update',
          table: 'pedidoCompra',
          data: {
            pedidoCompra_id: pedidoCompraId,
            orcItem_id: data.orcItem_id,
            pedidoCompra_vlrLimite: data.pedidoCompra_vlrLimite || 0,
            pedidoCompra_dtLimiteEntrega: data.pedidoCompra_dtLimiteEntrega || undefined,
            pedidoCompra_dtVigIni: data.pedidoCompra_dtVigIni || undefined,
            pedidoCompra_dtVigFim: fimVigenciaParaGravar(data) || undefined,
            pedidoCompra_desc: data.pedidoCompra_desc.trim(),
            pedidoCompra_justificativa: String(data.pedidoCompra_justificativa || '').trim() || null,
            pedidoCompraStatus_id: PEDIDO_COMPRA_STATUS_APROVADO,
            pf_id: orcItemPfId,
          },
        })

        if (filesPedidoCompra.length > 0) {
          await savePedidoCompraOuVagaDocuments(filesPedidoCompra, pedidoCompraId, 'pedidoCompra')
        }

        await updateVaga({ ...data, pedidoCompra_id: pedidoCompraId })
        const vagaId = await resolveVagaIdAfterSave({
          pedidoCompraId,
          currentVagaId: data.vaga_id,
        })
        if (Array.isArray(data.vagaHorarios)) {
          try {
            await persistVagaHorarios(vagaId, data.vagaHorarios)
          } catch (horarioError) {
            console.log('Erro ao gravar horários da vaga:', horarioError)
            notifyError('A vaga foi salva, mas os horários da semana não foram gravados.')
          }
        }
        const inhireResult = createInhire
          ? await createAndPersistInhireJobFromForm({
            vagaId,
            existingInhireId: data.vaga_inhireId,
            inhireForm,
          })
          : { ok: true, skipped: true, inhireJobId: data.vaga_inhireId || null }

        if (onSuccess) {
          await onSuccess({
            pedidoCompra_id: pedidoCompraId,
            vaga_id: vagaId,
            vaga_inhireId: inhireResult.inhireJobId || data.vaga_inhireId,
          })
        }
        if (onClose) onClose()
        if (inhireResult.ok && !inhireResult.skipped && !inhireResult.alreadyExists) {
          notifySuccess('Vaga publicada no Oikos e aberta no inHire.')
        } else if (inhireResult.skipped || inhireResult.alreadyExists) {
          notifySuccess(showEquipePedidoCompraFields ? 'Vaga publicada no Oikos.' : 'Cotação aprovado com sucesso.')
        }
        return inhireResult
      }

      const postBody = {
        action: 'insert',
        table: 'pedidoCompra',
        data: {
          orcItem_id: data.orcItem_id,
          pedidoCompra_vlrLimite: data.pedidoCompra_vlrLimite || 0,
          pedidoCompra_dtLimiteEntrega: data.pedidoCompra_dtLimiteEntrega || undefined,
          pedidoCompra_dtVigIni: data.pedidoCompra_dtVigIni || undefined,
          pedidoCompra_dtVigFim: fimVigenciaParaGravar(data) || undefined,
          pedidoCompra_dtSolicitacao: moment().format('YYYY-MM-DD'),
          pedidoCompra_desc: data.pedidoCompra_desc.trim(),
          pedidoCompra_justificativa: String(data.pedidoCompra_justificativa || '').trim() || null,
          pedidoCompraStatus_id: PEDIDO_COMPRA_STATUS_APROVADO,
          pf_id: orcItemPfId,
        },
      }

      const response = await oikosApi.post('/', postBody)
      const newPedidoCompraId = response?.data?.[0]?.pedidoCompra_id

      if (newPedidoCompraId && filesPedidoCompra.length > 0) {
        await savePedidoCompraOuVagaDocuments(filesPedidoCompra, newPedidoCompraId, 'pedidoCompra')
      }

      const insertResponse = await insertVaga({ ...data, pedidoCompra_id: newPedidoCompraId })
      const vagaId = await resolveVagaIdAfterSave({
        pedidoCompraId: newPedidoCompraId,
        insertResponse,
        currentVagaId: data.vaga_id,
      })
      if (Array.isArray(data.vagaHorarios)) {
        try {
          await persistVagaHorarios(vagaId, data.vagaHorarios)
        } catch (horarioError) {
          console.log('Erro ao gravar horários da vaga:', horarioError)
          notifyError('A vaga foi salva, mas os horários da semana não foram gravados.')
        }
      }
      const inhireResult = createInhire
        ? await createAndPersistInhireJobFromForm({
          vagaId,
          existingInhireId: data.vaga_inhireId,
          inhireForm,
        })
        : { ok: true, skipped: true, inhireJobId: null }

      if (onSuccess) {
        await onSuccess({
          pedidoCompra_id: newPedidoCompraId,
          vaga_id: vagaId,
          vaga_inhireId: inhireResult.inhireJobId,
        })
      }
      if (onClose) onClose()
      if (inhireResult.ok && !inhireResult.skipped && !inhireResult.alreadyExists) {
        notifySuccess('Vaga publicada no Oikos e aberta no inHire.')
      } else if (inhireResult.skipped || inhireResult.alreadyExists) {
        notifySuccess(showEquipePedidoCompraFields ? 'Vaga publicada no Oikos.' : 'Cotação aprovado com sucesso.')
      }
      return inhireResult
    } catch (e) {
      notifyError(`Erro ao cadastrar ${showEquipePedidoCompraFields ? 'vaga' : 'cotação'}.`)
      console.log(`Erro ao inserir ${showEquipePedidoCompraFields ? 'vaga' : 'pedidoCompra'}:`, e)
      return null
    }
  }, [
    pedidoCompraData,
    filesPedidoCompra,
    showEquipePedidoCompraFields,
    onSuccess,
    onClose,
  ])

  const salvarAlocacaoInterna = useCallback(async () => {
    const orcItemId = Number(pedidoCompraData?.orcItem_id) || 0
    const contratoFornId = Number(pedidoCompraData?.contratoForn_id) || 0

    if (!orcItemId) {
      notifyError('Nao foi possivel identificar o item orcamentario.')
      return
    }

    if (!contratoFornId) {
      notifyError('Selecione um contrato interno.')
      return
    }

    try {
      setIsSavingContratoInterno(true)

      await oikosApi.post('/', {
        action: 'update',
        table: 'orcItem',
        data: {
          orcItem_id: orcItemId,
          contratoForn_id: contratoFornId,
        },
      })

      const selectedOption = selContratosInternos.find(
        (option) => Number(option.value) === contratoFornId,
      )

      setPedidoCompraData?.((prev) => ({
        ...prev,
        contratoForn_id: contratoFornId,
        contratoForn_idLocked: true,
        contratoForn_label: selectedOption?.label ?? prev.contratoForn_label ?? null,
      }))

      if (onSuccess) {
        await onSuccess({
          orcItem_id: orcItemId,
          contratoForn_id: contratoFornId,
          contratoForn_label: selectedOption?.label ?? null,
        })
      }
      if (onClose) onClose()
      notifySuccess('Alocacao interna salva com sucesso.')
    } catch (e) {
      notifyError('Erro ao salvar alocacao interna.')
      console.log('Erro ao atualizar contratoForn_id do orcItem:', e)
    } finally {
      setIsSavingContratoInterno(false)
    }
  }, [pedidoCompraData, setPedidoCompraData, selContratosInternos, onSuccess, onClose])

  const abrirVagaInhire = useCallback(async () => {
    if (!pedidoCompraData?.orcItem_id) {
      notifyError('Nao foi possivel identificar o item orcamentario.')
      return
    }

    const jobName = buildInhireJobName(pedidoCompraData)
    if (!jobName) {
      notifyError('Preencha a atuacao ou o objeto da vaga.')
      return
    }

    if (!pedidoCompraData?.pedidoCompra_desc?.trim() && !pedidoCompraData?.vaga_descricao?.trim()) {
      notifyError('Preencha o objeto da vaga.')
      return
    }

    if (!pedidoCompraData?.vagaEstado || !pedidoCompraData?.vagaCidade) {
      notifyError('Preencha estado e cidade da vaga.')
      return
    }

    if (vagaTipo === 'interno' && !normalizeOrcItemId(pedidoCompraData?.orcItem_original)) {
      notifyError('Selecione o item orcamentario de origem.')
      return
    }

    try {
      setIsCreatingInhireVaga(true)

      if (vagaTipo === 'interno') {
        const updated = await updateOrcItemOriginal()
        if (!updated) return
      }

      await submitPedidoCompraEquipe()
    } finally {
      setIsCreatingInhireVaga(false)
    }
  }, [pedidoCompraData, submitPedidoCompraEquipe, updateOrcItemOriginal, vagaTipo])

  const abrirVagaPedidoCompra = useCallback(async (inhireForm = null, { createInhire = false } = {}) => {
    try {
      setIsSavingPedidoCompraEquipe(true)

      const hydratedForm = inhireForm
        ? await hydrateInhireFormLocation(inhireForm)
        : null
      const validationError = createInhire
        ? validateForm(hydratedForm || {})
        : validateOikosForm(hydratedForm || {})
      if (validationError) {
        notifyError(validationError)
        return
      }

      if (vagaTipo === 'interno') {
        const updated = await updateOrcItemOriginal()
        if (!updated) return
      }

      await submitPedidoCompraEquipe(hydratedForm, { createInhire })
    } finally {
      setIsSavingPedidoCompraEquipe(false)
    }
  }, [submitPedidoCompraEquipe, updateOrcItemOriginal, vagaTipo])

  return {
    selContratosInternos,
    selOrcItemsOriginais,
    isLoadingContratosInternos,
    isLoadingOrcItemsOriginais,
    isSavingContratoInterno,
    isSavingPedidoCompraEquipe,
    isCreatingInhireVaga,
    loadContratosInternos,
    loadOrcItemsOriginais,
    salvarAlocacaoInterna,
    abrirVagaPedidoCompra,
    abrirVagaInhire,
    submitConsultor: submitPedidoCompraEquipe,
  }
}

export {
  savePedidoCompraOuVagaDocuments,
  insertVaga,
  updateVaga,
  buildVagaDataFromPedidoCompra,
  fetchVagaByPedidoCompraId,
  resolveVagaIdForEquipePedidoCompra,
  insertCandidatoFromCompraCotacao,
  fetchOrcItemContratoFornId,
  fetchProjAtuacaoIdForOrcItem,
  fetchPedidoCompraVagaInhireData,
  abrirVagaInhireParaPedidoCompra,
  VIEW_RUBR_ORC_ITEM_PROJ_ATUACAO_ID_FIELD,
}
